import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { useNavigate } from "react-router-dom";
import { OuterContentContainer } from "@shared/ui/OuterContentContainer";
import { Loader } from "@shared/ui/Loader";
import { AppPageHeader } from "@shared/ui/AppPageHeader";
import { Article, Category } from "../../support.types";
import { SupportSidebar } from "../SupportSidebar/SupportSidebar";
import { SupportSidebarSearchButton } from "../SupportSidebar/SupportSidebarSearchButton";
import { SupportSidebarSupportButton } from "../SupportSidebar/SupportSidebarSupportButton";
import SupportCategoryAccordion from "../SupportCategoryAccordion/SupportCategoryAccordion";
import SupportCategoryAccordionButton from "../SupportCategoryAccordion/SupportCategoryAccordionButton";

export type Props = {
  articles: Article[];
  categories: Category[];
  isLoading: boolean;
  title?: string;
  description?: string;
  children: React.ReactNode;
  isArticlePage?: boolean;
  onSearchClick: () => void;
  onSupportClick: () => void;
  onNavigationClick?: (article: Article) => void;
};

export function Support({
  articles,
  categories,
  title,
  description,
  isArticlePage = false,
  isLoading,
  children,
  onSearchClick,
  onSupportClick,
  onNavigationClick,
}: Props) {
  const navigate = useNavigate();

  const [isCategoriesPanelOpen, setIsCategoriesPanelOpen] = useState(false);

  const handleNavigationClick = (article: Article) => {
    navigate(`/support/${article.id}`);
  };

  return (
    <>
      <OuterContentContainer
        className="flex flex-1 md:py-0"
        innerClassName="flex-1 flex-col px-0 md:flex-row md:h-full md:items-stretch"
      >
        {isLoading || !articles ? (
          <Loader isFullPage />
        ) : (
          <>
            <div
              className={twMerge(
                "flex flex-col flex-1 py-8 gap-8",
                isArticlePage && "gap-0"
              )}
            >
              <div
                className={twMerge(
                  "flex flex-col gap-4 py-4",
                  isArticlePage && "flex-col-reverse gap-12 py-0"
                )}
              >
                <AppPageHeader
                  title={title}
                  titleTypeVariant="h2"
                  description={description}
                  className="px-4 md:px-0 mb-12 md:max-w-3xl"
                />
                <div className="md:hidden">
                  <div className="flex flex-col gap-2 px-4">
                    <SupportSidebarSearchButton onClick={onSearchClick} />
                    <div className="flex flex-row gap-2 w-full">
                      <SupportCategoryAccordionButton
                        className="flex-[0_0_calc(50%-0.25rem)]"
                        onClick={() =>
                          setIsCategoriesPanelOpen(!isCategoriesPanelOpen)
                        }
                      />
                      <SupportSidebarSupportButton
                        onClick={onSupportClick}
                        className="flex-[0_0_calc(50%-0.25rem)]"
                      />
                    </div>
                  </div>
                  <SupportCategoryAccordion
                    categories={categories}
                    isOpen={isCategoriesPanelOpen}
                    setIsOpen={setIsCategoriesPanelOpen}
                    onNavigationClick={handleNavigationClick}
                  />
                </div>
              </div>
              {children}
            </div>
            <SupportSidebar
              articles={articles || []}
              categories={categories}
              onSearchClick={onSearchClick}
              onSupportClick={onSupportClick}
              onNavigationClick={onNavigationClick}
            />
          </>
        )}
      </OuterContentContainer>
    </>
  );
}

export default Support;
